import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  listRestaurants,
  updateRestaurantMinimumOrder,
  type RestaurantRecord,
} from '../../services/restaurants';

const currencyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  maximumFractionDigits: 2,
});

const formatCurrency = (value: number | null) =>
  value !== null ? currencyFormatter.format(value) : 'Limit yok';

const parseInputValue = (raw: string | undefined) => {
  if (!raw || raw.trim() === '') {
    return { value: null, isValid: true } as const;
  }

  const normalized = raw.replace(',', '.');
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { value: null, isValid: false } as const;
  }

  return { value: Math.round(parsed * 100) / 100, isValid: true } as const;
};

type Feedback = { type: 'success' | 'error'; text: string } | null;

export default function RestaurantMinimums() {
  const queryClient = useQueryClient();
  const {
    data: restaurants = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['operations', 'restaurants', 'minimums'],
    queryFn: listRestaurants,
  });

  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [inputErrors, setInputErrors] = useState<Record<string, string | null>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);

  useEffect(() => {
    if (restaurants.length === 0) {
      setFormValues({});
      return;
    }

    const nextValues: Record<string, string> = {};
    restaurants.forEach((restaurant) => {
      nextValues[restaurant.id] =
        restaurant.minimumOrderAmount !== null
          ? restaurant.minimumOrderAmount.toString()
          : '';
    });
    setFormValues(nextValues);
    setInputErrors({});
  }, [restaurants]);

  useEffect(() => {
    if (!feedback) return;
    const timeout = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timeout);
  }, [feedback]);

  const mutation = useMutation({
    mutationFn: updateRestaurantMinimumOrder,
    onMutate: (variables) => {
      setActiveId(variables.id);
    },
    onSuccess: (updated) => {
      setFeedback({
        type: 'success',
        text: `${updated.name} için limit güncellendi.`,
      });
      setFormValues((prev) => ({
        ...prev,
        [updated.id]:
          updated.minimumOrderAmount !== null
            ? updated.minimumOrderAmount.toString()
            : '',
      }));
      void queryClient.invalidateQueries({
        queryKey: ['operations', 'restaurants', 'minimums'],
      });
    },
    onError: (error: unknown) => {
      setFeedback({
        type: 'error',
        text: error instanceof Error ? error.message : 'Güncelleme sırasında bir hata oluştu.',
      });
    },
    onSettled: () => {
      setActiveId(null);
    },
  });

  const handleInputChange = (id: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [id]: value,
    }));
    setInputErrors((prev) => ({ ...prev, [id]: null }));
  };

  const handleSave = (restaurant: RestaurantRecord) => {
    const rawValue = formValues[restaurant.id];
    const { value, isValid } = parseInputValue(rawValue);

    if (!isValid) {
      setInputErrors((prev) => ({
        ...prev,
        [restaurant.id]: 'Geçerli bir tutar girin (örn. 250 veya 199.90)',
      }));
      return;
    }

    mutation.mutate({ id: restaurant.id, amount: value });
  };

  const handleReset = (restaurant: RestaurantRecord) => {
    setFormValues((prev) => ({
      ...prev,
      [restaurant.id]:
        restaurant.minimumOrderAmount !== null
          ? restaurant.minimumOrderAmount.toString()
          : '',
    }));
    setInputErrors((prev) => ({ ...prev, [restaurant.id]: null }));
  };

  const hasChanges = (restaurant: RestaurantRecord) => {
    const rawValue = formValues[restaurant.id];
    const { value, isValid } = parseInputValue(rawValue);
    if (!isValid) return false;
    const current = restaurant.minimumOrderAmount;
    return value !== current;
  };

  const isEmpty = !isLoading && restaurants.length === 0;

  const savingId = activeId;

  const deliverySummary = useMemo(() => {
    if (restaurants.length === 0) return 0;
    return restaurants.filter((r) => r.acceptsDelivery).length;
  }, [restaurants]);

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <p className="card-title">Minimum sipariş limitleri</p>
          <p className="card-subtitle">
            Teslimat bölgeleri için alt limit belirleyin. Listede {restaurants.length} restoran, {deliverySummary}{' '}
            tanesi teslimat kabul ediyor.
          </p>
        </div>
        <button
          type="button"
          className="button ghost"
          onClick={() => {
            setFeedback(null);
            void refetch();
          }}
          disabled={isFetching}
        >
          {isFetching ? 'Yükleniyor…' : 'Yenile'}
        </button>
      </div>

      {feedback ? (
        <div className={`status-banner ${feedback.type}`}>
          {feedback.text}
        </div>
      ) : null}

      {isLoading ? (
        <div className="placeholder-card">Restoran verileri yükleniyor…</div>
      ) : isEmpty ? (
        <div className="empty-state">Henüz restoran kaydı bulunamadı.</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table" aria-label="Minimum sipariş ayarları">
            <thead>
              <tr>
                <th>Restoran</th>
                <th>Servis durumu</th>
                <th>Geçerli limit</th>
                <th>Yeni limit (₺)</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {restaurants.map((restaurant) => {
                const currentValue = formValues[restaurant.id] ?? '';
                const errorText = inputErrors[restaurant.id];
                const dirty = hasChanges(restaurant);
                const isSaving = savingId === restaurant.id && mutation.isPending;

                return (
                  <tr key={restaurant.id}>
                    <td>
                      <strong>{restaurant.name}</strong>
                      <div className="helper-text">
                        {restaurant.city} / {restaurant.district} · {restaurant.slug}
                      </div>
                    </td>
                    <td>
                      <div className="chip-group">
                        <span className={`chip ${restaurant.acceptsDelivery ? 'success' : 'neutral'}`}>
                          Teslimat
                        </span>
                        <span className={`chip ${restaurant.acceptsTakeaway ? 'success' : 'neutral'}`}>
                          Gel-al
                        </span>
                        <span className={`chip ${restaurant.isAcceptingOrders ? 'success' : 'warning'}`}>
                          {restaurant.isAcceptingOrders ? 'Sipariş alıyor' : 'Kapalı'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div>{formatCurrency(restaurant.minimumOrderAmount)}</div>
                      <div className="helper-text">
                        {restaurant.minimumOrderAmount === null
                          ? 'Limit uygulanmıyor'
                          : 'Değeri değiştirip kaydedebilirsiniz'}
                      </div>
                    </td>
                    <td>
                      <div className="input-stack">
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.5"
                          className="inline-input"
                          value={currentValue}
                          onChange={(event) => handleInputChange(restaurant.id, event.target.value)}
                          placeholder="Örn. 250"
                          aria-label={`${restaurant.name} yeni minimum tutarı`}
                        />
                        <div className="helper-text">Boş bırakırsanız limit kaldırılır.</div>
                        {errorText ? <div className="error-text">{errorText}</div> : null}
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="button primary"
                          disabled={isSaving || !dirty}
                          onClick={() => handleSave(restaurant)}
                        >
                          {isSaving ? 'Kaydediliyor…' : 'Kaydet'}
                        </button>
                        <button
                          type="button"
                          className="button ghost"
                          disabled={isSaving || (!dirty && currentValue === '')}
                          onClick={() => handleReset(restaurant)}
                        >
                          Sıfırla
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
